/**
 * Recovery Calculator
 * 
 * Calculates daily readiness score based on multiple recovery metrics.
 * Uses weighted algorithm to determine if athlete should train, deload, or rest.
 * 
 *  REFACTORED: Now saves to Dexie (IndexedDB) in addition to Firebase
 */

import type { RecoveryMetrics, ReadinessCalculation, RecoveryInsight } from './types/recovery';
import { saveRecoveryToFirebase, getRecoveryHistoryFromFirebase } from './firebaseSync';
import { getCurrentUserId } from './anonymousAuth';
import { db, DBRecoveryEntry } from './db';
import { storage } from './storage';
import type { PersonalState } from './personalState';

// ============================================================================
// RECOVERY CACHE - Prevents redundant Dexie queries within a page load
// ============================================================================
interface RecoveryCache {
  data: RecoveryMetrics[] | null;
  userId: string | null;
  timestamp: number;
}

const recoveryCache: RecoveryCache = {
  data: null,
  userId: null,
  timestamp: 0,
};

// Cache TTL in milliseconds (5 seconds)
const RECOVERY_CACHE_TTL = 5000;

/**
 * Invalidate the recovery cache. Call this when recovery data is modified.
 */
export function invalidateRecoveryCache(): void {
  recoveryCache.data = null;
  recoveryCache.userId = null;
  recoveryCache.timestamp = 0;
}

// Weights for each metric (sum to 1.0)
const METRIC_WEIGHTS = {
  sleep: 0.30,          // Most important
  soreness: 0.25,       // Physical readiness
  energy: 0.15,         // Subjective energy
  motivation: 0.10,     // Mental readiness
  stress: 0.10,         // Life stress impact
  hrv: 0.05,            // Advanced metric
  restingHR: 0.05,      // Advanced metric
};

/**
 * Calculate readiness score from recovery metrics.
 *
 * As of v1.9.14, this blends a subjective check-in score (sleep,
 * soreness, energy, motivation, stress + raw HRV/RHR readings) with
 * the objective PersonalState score (per-user wearable baseline
 * deviation). The objective weight climbs with PersonalState
 * confidence:
 *
 *   - No wearable history    → 100% subjective (legacy behavior)
 *   - 14-30 days wearable    → ramping toward 50/50
 *   - 30+ days wearable      → up to 70% objective + 30% subjective
 *
 * Callers can pass a pre-loaded PersonalState to avoid double-fetch.
 * Otherwise the function loads it lazily (silently skipped if user
 * isn't signed in or has no wearable data).
 */
export async function calculateReadinessScore(
  metrics: RecoveryMetrics,
  personalState?: PersonalState | null,
): Promise<ReadinessCalculation> {
  // Load recovery history once for baseline calculations
  const history = await getRecoveryHistory();
  
  let totalScore = 0;
  let totalWeight = 0;
  
  // Sleep score (0-100)
  const sleepScore = calculateSleepScore(metrics.sleepHours, metrics.sleepQuality);
  totalScore += sleepScore * METRIC_WEIGHTS.sleep;
  totalWeight += METRIC_WEIGHTS.sleep;
  
  // Soreness score (inverse - less is better)
  const sorenessScore = (10 - metrics.overallSoreness) * 10; // Convert to 0-100
  totalScore += sorenessScore * METRIC_WEIGHTS.soreness;
  totalWeight += METRIC_WEIGHTS.soreness;
  
  // Energy score
  const energyScore = metrics.energy * 10; // Convert 1-10 to 0-100
  totalScore += energyScore * METRIC_WEIGHTS.energy;
  totalWeight += METRIC_WEIGHTS.energy;
  
  // Motivation score
  const motivationScore = metrics.motivation * 10;
  totalScore += motivationScore * METRIC_WEIGHTS.motivation;
  totalWeight += METRIC_WEIGHTS.motivation;
  
  // Stress score (inverse - less is better)
  const stressScore = (10 - metrics.stress) * 10;
  totalScore += stressScore * METRIC_WEIGHTS.stress;
  totalWeight += METRIC_WEIGHTS.stress;
  
  // Optional: HRV score
  if (metrics.hrv !== undefined && metrics.hrv > 0) {
    const hrvScore = calculateHRVScore(metrics.hrv, history);
    totalScore += hrvScore * METRIC_WEIGHTS.hrv;
    totalWeight += METRIC_WEIGHTS.hrv;
  }
  
  // Optional: Resting HR score
  if (metrics.restingHR !== undefined && metrics.restingHR > 0) {
    const rhrScore = calculateRestingHRScore(metrics.restingHR, history);
    totalScore += rhrScore * METRIC_WEIGHTS.restingHR;
    totalWeight += METRIC_WEIGHTS.restingHR;
  }
  
  // Normalize score
  let finalScore = Math.round((totalScore / totalWeight));

  // Apply NEAT readiness modifier (additive, max +/-5 points)
  // Non-critical: wrapped in try/catch, fails silently for users without wearables
  try {
    const { getTodayActivity, getDailyActivityEntries } = await import('./db');
    const { calculateNEATReadinessModifier, analyzeActivityTrend } = await import('./neatCalculator');
    const userId = getCurrentUserId();
    if (userId) {
      const todayActivity = await getTodayActivity(userId);
      if (todayActivity) {
        // Load 30d entries for trend
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const entries = await getDailyActivityEntries(userId, { since: thirtyDaysAgo });
        const trend = entries.length >= 7 ? analyzeActivityTrend(entries as any) : null;
        const neatModifier = calculateNEATReadinessModifier(todayActivity as any, trend);
        if (neatModifier !== 0) {
          finalScore = Math.max(0, Math.min(100, finalScore + neatModifier));
          console.log(`NEAT readiness modifier: ${neatModifier > 0 ? '+' : ''}${neatModifier} (final: ${finalScore})`);
        }
      }
    }
  } catch {
    // Non-critical: users without wearables see zero changes
  }

  // Snapshot the subjective+raw-wearable score BEFORE the blend so the
  // blend metadata can decompose what changed.
  const subjectiveScore = finalScore;

  // PersonalState blend — when available + confidence ≥0.40, blend the
  // per-user baseline-relative score into the subjective check-in.
  // Weight ramps with confidence (capped at 0.70 so subjective always
  // matters somewhat — the user's sense of self is signal too).
  let blend: ReadinessCalculation['blend'] = undefined;
  let psState: PersonalState | null = personalState ?? null;
  if (psState === undefined || psState === null) {
    try {
      const userId = getCurrentUserId();
      if (userId) {
        const { loadPersonalState } = await import('./personalState');
        // Best-effort age lookup — falls back to null (PersonalState
        // uses 30y default for HRmax, fine for the readiness blend).
        let ageYears: number | null = null;
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db: fdb } = await import('./firebase');
          const snap = await getDoc(doc(fdb, 'users', userId));
          const data = snap.exists() ? snap.data() : null;
          if (typeof data?.age === 'number' && data.age > 0) ageYears = data.age;
        } catch {}
        psState = await loadPersonalState(userId, ageYears);
      }
    } catch {
      // Non-critical — fall back to subjective-only
    }
  }

  if (psState && psState.overallConfidence >= 0.40) {
    const objectiveWeight = Math.min(0.70, psState.overallConfidence * 0.70);
    const subjectiveWeight = 1 - objectiveWeight;
    const blended = (subjectiveScore * subjectiveWeight) + (psState.overallReadiness * objectiveWeight);
    finalScore = Math.round(Math.max(0, Math.min(100, blended)));
    blend = {
      applied: true,
      objectiveWeight,
      subjectiveScore,
      objectiveScore: psState.overallReadiness,
      confidence: psState.overallConfidence,
    };
  } else if (psState) {
    // PersonalState exists but confidence too low — surface this so
    // UIs can still show "we have some data but not enough to blend."
    blend = {
      applied: false,
      objectiveWeight: 0,
      subjectiveScore,
      objectiveScore: psState.overallReadiness,
      confidence: psState.overallConfidence,
    };
  }

  // Determine category and recommendations
  const result = categorizeReadiness(finalScore, metrics);
  if (blend) result.blend = blend;
  return result;
}

/**
 * Calculate sleep score based on hours and quality
 */
function calculateSleepScore(hours: number, quality: number): number {
  // Optimal sleep: 7-9 hours
  let hoursScore: number;
  
  if (hours < 5) {
    hoursScore = 30; // Very poor
  } else if (hours < 6) {
    hoursScore = 50; // Poor
  } else if (hours < 7) {
    hoursScore = 70; // Fair
  } else if (hours <= 9) {
    hoursScore = 100; // Excellent
  } else if (hours <= 10) {
    hoursScore = 90; // Good (slightly long)
  } else {
    hoursScore = 70; // Too much sleep can indicate fatigue
  }
  
  // Quality (1-10 scale to 0-100)
  const qualityScore = quality * 10;
  
  // Weighted average (hours more important)
  return (hoursScore * 0.6) + (qualityScore * 0.4);
}

/**
 * Get user's personalized HRV baseline from history
 * Uses first 14 days of data to establish baseline
 */
function getUserHRVBaseline(history: RecoveryMetrics[]): number | null {
  if (typeof window === 'undefined') return null;
  const validHRVReadings = history
    .filter(m => m.hrv !== undefined && m.hrv > 0)
    .slice(0, 14); // First 14 readings
  
  if (validHRVReadings.length < 7) {
    return null; // Need at least 7 readings to establish baseline
  }
  
  const avgHRV = validHRVReadings.reduce((sum, m) => sum + (m.hrv || 0), 0) / validHRVReadings.length;
  return avgHRV;
}

/**
 * Get user's personalized RHR baseline from history
 * Uses first 14 days of data to establish baseline
 */
function getUserRHRBaseline(history: RecoveryMetrics[]): number | null {
  if (typeof window === 'undefined') return null;
  const validRHRReadings = history
    .filter(m => m.restingHR !== undefined && m.restingHR > 0)
    .slice(0, 14); // First 14 readings
  
  if (validRHRReadings.length < 7) {
    return null; // Need at least 7 readings to establish baseline
  }
  
  const avgRHR = validRHRReadings.reduce((sum, m) => sum + (m.restingHR || 0), 0) / validRHRReadings.length;
  return avgRHR;
}

/**
 * Calculate HRV score (higher is better, relative to user's baseline)
 * Uses personalized baseline if available, otherwise falls back to population average
 */
function calculateHRVScore(hrv: number, history: RecoveryMetrics[]): number {
  const baseline = getUserHRVBaseline(history);
  
  if (baseline) {
    // Personalized scoring based on % of baseline
    const percentOfBaseline = (hrv / baseline) * 100;
    
    if (percentOfBaseline < 70) return 20;      // 30%+ below baseline - very low
    if (percentOfBaseline < 85) return 40;      // 15-30% below - low
    if (percentOfBaseline < 95) return 60;      // 5-15% below - below average
    if (percentOfBaseline <= 105) return 80;    // ±5% of baseline - good
    if (percentOfBaseline <= 115) return 90;    // 5-15% above - very good
    return 100;                                  // 15%+ above baseline - excellent
  }
  
  // Fallback: Generic population-based scoring
  if (hrv < 20) return 20;      // Very low
  if (hrv < 40) return 40;      // Low
  if (hrv < 60) return 60;      // Below average
  if (hrv < 80) return 80;      // Good
  if (hrv < 100) return 90;     // Very good
  return 100;                    // Excellent
}

/**
 * Calculate Resting HR score (lower is better, relative to user's baseline)
 * Uses personalized baseline if available, otherwise falls back to population average
 */
function calculateRestingHRScore(rhr: number, history: RecoveryMetrics[]): number {
  const baseline = getUserRHRBaseline(history);
  
  if (baseline) {
    // Personalized scoring based on % of baseline
    const percentOfBaseline = (rhr / baseline) * 100;
    
    if (percentOfBaseline > 115) return 20;     // 15%+ above baseline - very high (possible illness)
    if (percentOfBaseline > 110) return 40;     // 10-15% above - high
    if (percentOfBaseline > 105) return 60;     // 5-10% above - elevated
    if (percentOfBaseline >= 95) return 80;     // ±5% of baseline - good
    if (percentOfBaseline >= 90) return 90;     // 5-10% below - very good
    return 100;                                  // 10%+ below baseline - excellent
  }
  
  // Fallback: Generic population-based scoring
  if (rhr < 50) return 100;     // Excellent
  if (rhr < 60) return 90;      // Very good
  if (rhr < 70) return 80;      // Good
  if (rhr < 80) return 60;      // Elevated
  if (rhr < 90) return 40;      // High
  return 20;                     // Very high - possible overtraining
}

/**
 * Categorize readiness and provide recommendations
 */
function categorizeReadiness(score: number, metrics: RecoveryMetrics): ReadinessCalculation {
  let category: 'Poor' | 'Fair' | 'Good' | 'Excellent';
  let recommendation: string;
  let shouldDeload: boolean;
  let volumeAdjustment: number;
  let intensityAdjustment: number;
  
  if (score >= 85) {
    category = 'Excellent';
    recommendation = 'Excellent recovery! You\'re ready for a challenging session. Consider pushing for PRs.';
    shouldDeload = false;
    volumeAdjustment = 5;  // Can add 5% volume
    intensityAdjustment = 5; // Can add 5% intensity
  } else if (score >= 70) {
    category = 'Good';
    recommendation = 'Good recovery. Proceed with planned training as programmed.';
    shouldDeload = false;
    volumeAdjustment = 0;
    intensityAdjustment = 0;
  } else if (score >= 50) {
    category = 'Fair';
    recommendation = 'Moderate recovery. Consider reducing volume by 15-20% or intensity by 10-15%. Focus on technique.';
    shouldDeload = false;
    volumeAdjustment = -15;
    intensityAdjustment = -10;
  } else {
    category = 'Poor';
    recommendation = 'Poor recovery detected. Strong recommendation to deload or take active recovery day. Your body needs rest.';
    shouldDeload = true;
    volumeAdjustment = -30;
    intensityAdjustment = -20;
  }
  
  // Additional specific warnings
  if (metrics.sleepHours < 6) {
    recommendation += ' SLEEP DEFICIT: Prioritize sleep tonight.';
  }
  
  if (metrics.overallSoreness >= 8) {
    recommendation += ' HIGH SORENESS: Extra mobility work recommended.';
  }
  
  if (metrics.stress >= 8) {
    recommendation += ' HIGH STRESS: Consider stress management techniques (breathing, meditation).';
  }
  
  return {
    score,
    category,
    recommendation,
    shouldDeload,
    volumeAdjustment,
    intensityAdjustment
  };
}

/**
 * Generate actionable insights from recovery data
 */
export async function generateRecoveryInsights(metrics: RecoveryMetrics): Promise<RecoveryInsight[]> {
  // Load recovery history for baseline calculations
  const history = await getRecoveryHistory();
  const insights: RecoveryInsight[] = [];
  
  // Sleep insights
  if (metrics.sleepHours < 6) {
    insights.push({
      type: 'warning',
      title: 'Sleep Deficit',
      message: `You only got ${metrics.sleepHours} hours of sleep. Aim for 7-9 hours tonight.`,
      actionable: true,
      action: 'Set an earlier bedtime alarm'
    });
  } else if (metrics.sleepQuality < 5) {
    insights.push({
      type: 'warning',
      title: 'Poor Sleep Quality',
      message: 'Your sleep quality was low. Consider sleep hygiene improvements.',
      actionable: true,
      action: 'Review sleep environment (dark, cool, quiet)'
    });
  } else if (metrics.sleepHours >= 7 && metrics.sleepQuality >= 7) {
    insights.push({
      type: 'success',
      title: 'Great Sleep',
      message: 'Excellent sleep! Your body is recovering well.',
      actionable: false
    });
  }
  
  // Soreness insights
  if (metrics.overallSoreness >= 8) {
    insights.push({
      type: 'warning',
      title: 'High Soreness',
      message: 'Significant soreness detected. Extra recovery work recommended.',
      actionable: true,
      action: 'Add 15-20 min mobility + foam rolling'
    });
  }
  
  // Check specific soreness patterns
  const soreAreas = Object.entries(metrics.specificSoreness || {})
    .filter(([_, value]) => value && value >= 7)
    .map(([area]) => area);
  
  if (soreAreas.length > 0) {
    insights.push({
      type: 'info',
      title: 'Targeted Soreness',
      message: `High soreness in: ${soreAreas.join(', ')}. Consider targeted mobility.`,
      actionable: true,
      action: `Focus recovery work on ${soreAreas[0]}`
    });
  }
  
  // Energy insights
  if (metrics.energy <= 3 && metrics.motivation <= 3) {
    insights.push({
      type: 'warning',
      title: 'Low Energy & Motivation',
      message: 'Both energy and motivation are low. This may indicate overtraining or life stress.',
      actionable: true,
      action: 'Consider a full rest day or active recovery'
    });
  }
  
  // Stress insights
  if (metrics.stress >= 8) {
    insights.push({
      type: 'warning',
      title: 'High Stress Levels',
      message: 'High stress can impair recovery. Training may need to be adjusted.',
      actionable: true,
      action: '10 min box breathing before training'
    });
  }
  
  // HRV insights (if available)
  if (metrics.hrv !== undefined && metrics.hrv < 40) {
    insights.push({
      type: 'warning',
      title: 'Low HRV',
      message: 'Heart Rate Variability is low, indicating high stress on the nervous system.',
      actionable: true,
      action: 'Reduce training intensity today'
    });
  }
  
  // Resting HR insights (if available) - with illness detection
  if (metrics.restingHR !== undefined && metrics.restingHR > 0) {
    const baseline = getUserRHRBaseline(history);
    
    if (baseline && metrics.restingHR > baseline + 10) {
      // RHR > 10 bpm above baseline = possible illness
      insights.push({
        type: 'warning',
        title: ' Possible Illness Detected',
        message: `Resting HR is ${Math.round(metrics.restingHR - baseline)} bpm above your baseline (${Math.round(baseline)} bpm). This may indicate illness, not just fatigue.`,
        actionable: true,
        action: 'Take a rest day. Monitor for fever, sore throat, or other symptoms. Do NOT train if ill.'
      });
    } else if (metrics.restingHR > 80) {
      // Generic elevated RHR warning (no baseline available)
    insights.push({
      type: 'warning',
      title: 'Elevated Resting Heart Rate',
      message: 'Elevated RHR may indicate incomplete recovery or illness.',
      actionable: true,
      action: 'Monitor for illness symptoms, consider rest'
    });
    }
  }
  
  // Positive reinforcement
  const readiness = await calculateReadinessScore(metrics);
  if (readiness.score >= 85) {
    insights.push({
      type: 'success',
      title: 'Peak Readiness',
      message: 'All systems go! You\'re in excellent condition to train hard today.',
      actionable: false
    });
  }

  // Append NEAT / daily activity insights (non-critical, fails silently)
  try {
    const { getTodayActivity, getDailyActivityEntries } = await import('./db');
    const { generateActivityInsights, analyzeActivityTrend, detectNEATSuppression } = await import('./neatCalculator');
    const userId = getCurrentUserId();
    if (userId) {
      const todayActivity = await getTodayActivity(userId);
      if (todayActivity) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const entries = await getDailyActivityEntries(userId, { since: thirtyDaysAgo });
        const trend = entries.length >= 7 ? analyzeActivityTrend(entries as any) : null;
        const activityInsights = generateActivityInsights(todayActivity as any, trend);

        // Append activity insights
        for (const ai of activityInsights) {
          insights.push({
            type: ai.type === 'success' ? 'success' : ai.type === 'warning' ? 'warning' : 'info',
            title: ai.title,
            message: ai.message,
            actionable: ai.type === 'warning',
            action: ai.type === 'warning' ? 'Focus on light daily movement' : undefined,
          });
        }

        // Prepend NEAT suppression warning (high priority)
        if (entries.length >= 14) {
          const suppression = detectNEATSuppression(entries as any);
          if (suppression?.detected) {
            insights.unshift({
              type: 'warning',
              title: 'NEAT Suppression',
              message: suppression.message,
              actionable: true,
              action: 'Prioritize rest and light daily walking. Reduce training intensity.',
            });
          }
        }
      }
    }
  } catch {
    // Non-critical: users without wearables see zero changes
  }

  return insights;
}

/**
 * Compare recovery trend over time
 */
export function analyzeRecoveryTrend(recentMetrics: RecoveryMetrics[]): {
  trend: 'improving' | 'stable' | 'declining';
  averageScore: number;
  message: string;
} {
  if (recentMetrics.length < 3) {
    return {
      trend: 'stable',
      averageScore: recentMetrics[0]?.readinessScore || 70,
      message: 'Need more data to establish trend (minimum 3 days)'
    };
  }
  
  // Calculate average score
  const avgScore = recentMetrics.reduce((sum, m) => sum + m.readinessScore, 0) / recentMetrics.length;
  
  // Compare recent vs earlier
  const recent3 = recentMetrics.slice(0, 3);
  const earlier3 = recentMetrics.slice(3, 6);
  
  if (earlier3.length < 3) {
    return {
      trend: 'stable',
      averageScore: Math.round(avgScore),
      message: 'Establishing baseline...'
    };
  }
  
  const recentAvg = recent3.reduce((sum, m) => sum + m.readinessScore, 0) / 3;
  const earlierAvg = earlier3.reduce((sum, m) => sum + m.readinessScore, 0) / 3;
  
  const difference = recentAvg - earlierAvg;
  
  if (difference > 5) {
    return {
      trend: 'improving',
      averageScore: Math.round(avgScore),
      message: `Recovery improving! Up ${Math.round(difference)} points from last week.`
    };
  } else if (difference < -5) {
    return {
      trend: 'declining',
      averageScore: Math.round(avgScore),
      message: `Recovery declining. Down ${Math.round(Math.abs(difference))} points. Consider a deload week.`
    };
  } else {
    return {
      trend: 'stable',
      averageScore: Math.round(avgScore),
      message: 'Recovery stable and consistent.'
    };
  }
}

/**
 * Save recovery metrics to Firebase AND localStorage
 * Firebase is primary, localStorage is backup/cache
 * 
 * IMPORTANT: Only ONE entry per day. If today's entry exists, UPDATE it instead of creating new one.
 */
export async function saveRecoveryMetrics(metrics: RecoveryMetrics): Promise<void> {
  // Check if we're in browser environment
  if (typeof window === 'undefined') return;
  
  try {
    // Get current user ID (anonymous auth)
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn(' No user ID available for recovery, using anonymous');
    }
    
    // Check if today's entry already exists
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const existing = await getRecoveryHistory();
    const todayIndex = existing.findIndex(m => {
      const metricDate = new Date(m.date);
      metricDate.setHours(0, 0, 0, 0);
      return metricDate.getTime() === today.getTime();
    });
    
    let metricsWithUser: RecoveryMetrics;
    
    if (todayIndex >= 0) {
      // UPDATE existing entry (keep same ID for persistence)
      metricsWithUser = {
        ...metrics,
        id: existing[todayIndex].id, // Preserve existing ID
        userId: userId || 'anonymous',
        date: existing[todayIndex].date, // Preserve original date
      };
      existing[todayIndex] = metricsWithUser;
      console.log('� Updated existing recovery entry for today:', metricsWithUser.id);
    } else {
      // CREATE new entry
      metricsWithUser = {
        ...metrics,
        id: metrics.id || `recovery_${Date.now()}`,
        userId: userId || 'anonymous',
      };
    existing.push(metricsWithUser);
      console.log(' Created new recovery entry for today:', metricsWithUser.id);
    }
    
    //  Save to Dexie (primary local storage)
    const dbEntry: DBRecoveryEntry = {
      id: metricsWithUser.id,
      userId: metricsWithUser.userId,
      date: metricsWithUser.date,
      sleepHours: metricsWithUser.sleepHours,
      sleepQuality: metricsWithUser.sleepQuality,
      soreness: metricsWithUser.overallSoreness, // Map overallSoreness → soreness
      stress: metricsWithUser.stress,
      energy: metricsWithUser.energy,
      motivation: metricsWithUser.motivation,
      readinessScore: metricsWithUser.readinessScore || 0,
      notes: metricsWithUser.notes,
      // Persist the advanced inputs too — omitting these is what made manual
      // HRV / RHR / per-muscle soreness disappear on reload.
      specificSoreness: metricsWithUser.specificSoreness,
      hrv: metricsWithUser.hrv,
      restingHR: metricsWithUser.restingHR,
      bodyWeight: metricsWithUser.bodyWeight,
      hydration: metricsWithUser.hydration,
      nutritionAdherence: metricsWithUser.nutritionAdherence,
      syncStatus: 'local',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    await db.recovery.put(dbEntry);
    console.log('� Recovery saved to Dexie:', metricsWithUser.id);

    // Invalidate cache so next query gets fresh data
    invalidateRecoveryCache();

    // Sync to Firebase
    if (userId) {
      await saveRecoveryToFirebase(metricsWithUser);
      // Mark as synced in Dexie
      await db.recovery.update(metricsWithUser.id, { syncStatus: 'synced' });
      console.log('� Recovery synced to Firebase:', metricsWithUser.id);
    } else {
      console.warn(' Recovery not synced to Firebase (no user ID)');
    }
  } catch (error) {
    console.error(' Error saving recovery metrics:', error);
    throw error;
  }
}

/**
 * Sync recovery history from Firebase to Dexie
 *  REFACTORED: Now uses Dexie instead of localStorage
 * Call this on app initialization
 */
export async function syncRecoveryHistoryFromFirebase(): Promise<number> {
  const userId = getCurrentUserId();
  if (!userId) {
    console.log('� No user ID, skipping Firebase sync');
    return 0;
  }

  try {
    console.log('� Syncing recovery from Firebase to Dexie...');
    const { forceFullDownloadFromFirebase } = await import('./dexieFirebaseSync');
    const stats = await forceFullDownloadFromFirebase(userId);
    console.log(` Synced ${stats.recovery} recovery entries from Firebase to Dexie`);
    return stats.recovery;
  } catch (error) {
    console.error(' Failed to sync recovery from Firebase:', error);
    return 0;
  }
}

/**
 * Deduplicate recovery entries - keep only the latest entry per day
 */
function deduplicateRecoveryByDate(entries: RecoveryMetrics[]): RecoveryMetrics[] {
  const byDate = new Map<string, RecoveryMetrics>();
  
  entries.forEach(entry => {
    const date = new Date(entry.date);
    date.setHours(0, 0, 0, 0);
    const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
    
    const existing = byDate.get(dateKey);
    if (!existing) {
      byDate.set(dateKey, entry);
    } else {
      // Keep the entry with the later timestamp (most recent update)
      const existingTime = new Date(existing.date).getTime();
      const newTime = new Date(entry.date).getTime();
      if (newTime > existingTime) {
        byDate.set(dateKey, entry);
      }
    }
  });
  
  // Sort by date descending (newest first)
  return Array.from(byDate.values()).sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );
}

/**
 * Clean up duplicate recovery entries in Firebase
 * Keeps only the latest entry per day
 */
export async function cleanupDuplicateRecoveryEntries(): Promise<number> {
  const userId = getCurrentUserId();
  if (!userId) {
    console.log('� No user ID, skipping cleanup');
    return 0;
  }

  try {
    console.log('� Cleaning up duplicate recovery entries...');
    const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    
    const recoveryRef = collection(db, 'users', userId, 'recovery');
    const snapshot = await getDocs(recoveryRef);
    
    const entries: (RecoveryMetrics & { firestoreId: string })[] = [];
    snapshot.forEach(doc => {
      entries.push({ ...doc.data() as RecoveryMetrics, firestoreId: doc.id });
    });
    
    // Group by date
    const byDate = new Map<string, (RecoveryMetrics & { firestoreId: string })[]>();
    entries.forEach(entry => {
      // Handle both Firestore Timestamp and Date objects
      const entryDate: any = entry.date;
      const date = new Date(entryDate.seconds ? entryDate.seconds * 1000 : entryDate);
      date.setHours(0, 0, 0, 0);
      const dateKey = date.toISOString().split('T')[0];
      
      if (!byDate.has(dateKey)) {
        byDate.set(dateKey, []);
      }
      byDate.get(dateKey)!.push(entry);
    });
    
    // Delete duplicates (keep the latest)
    let deletedCount = 0;
    for (const [dateKey, dayEntries] of Array.from(byDate.entries())) {
      if (dayEntries.length > 1) {
        // Sort by timestamp, keep the latest
        dayEntries.sort((a, b) => {
          const aDate: any = a.date;
          const bDate: any = b.date;
          const aTime = aDate.seconds ? aDate.seconds * 1000 : new Date(aDate).getTime();
          const bTime = bDate.seconds ? bDate.seconds * 1000 : new Date(bDate).getTime();
          return bTime - aTime;
        });
        
        // Delete all but the first (latest)
        for (let i = 1; i < dayEntries.length; i++) {
          const docRef = doc(db, 'users', userId, 'recovery', dayEntries[i].firestoreId);
          await deleteDoc(docRef);
          deletedCount++;
          console.log(` Deleted duplicate recovery entry for ${dateKey}: ${dayEntries[i].firestoreId}`);
        }
      }
    }
    
    console.log(` Cleanup complete: ${deletedCount} duplicate entries removed`);
    return deletedCount;
  } catch (error) {
    console.error(' Failed to cleanup duplicates:', error);
    return 0;
  }
}

/**
 * Get recovery history from localStorage
 */
export async function getRecoveryHistory(): Promise<RecoveryMetrics[]> {
  // Check if we're in browser environment
  if (typeof window === 'undefined') {
    console.log('� getRecoveryHistory: SSR environment, returning empty array');
    return [];
  }

  try {
    const { db } = await import('./db');
    const { getCurrentUserId } = await import('./anonymousAuth');
    const userId = getCurrentUserId();

    if (!userId) {
      console.log('� getRecoveryHistory: No user ID, returning empty array');
      return [];
    }

    // Check cache first
    const now = Date.now();
    const cacheValid =
      recoveryCache.data !== null &&
      recoveryCache.userId === userId &&
      (now - recoveryCache.timestamp) < RECOVERY_CACHE_TTL;

    if (cacheValid) {
      console.log(`🩺 getRecoveryHistory: Using cached data (${recoveryCache.data!.length} entries)`);
      return recoveryCache.data!;
    }

    // Fetch from Dexie
    const entries = await db.recovery.where('userId').equals(userId).sortBy('date');
    console.log(`🩺 getRecoveryHistory: Loaded ${entries.length} recovery entries from Dexie`);

    // Convert DBRecoveryEntry to RecoveryMetrics
    const result = entries.map(entry => ({
      id: entry.id,
      userId: entry.userId,
      date: entry.date instanceof Date ? entry.date : new Date(entry.date),
      sleepHours: entry.sleepHours,
      sleepQuality: entry.sleepQuality,
      overallSoreness: (entry as any).overallSoreness || entry.soreness || 5, // Handle both field names
      specificSoreness: entry.specificSoreness || {},
      energy: entry.energy,
      motivation: entry.motivation,
      stress: entry.stress,
      readinessScore: entry.readinessScore,
      hrv: entry.hrv,
      restingHR: entry.restingHR,
      bodyWeight: entry.bodyWeight,
      hydration: entry.hydration,
      nutritionAdherence: entry.nutritionAdherence,
      notes: entry.notes,
    }));

    // Update cache
    recoveryCache.data = result;
    recoveryCache.userId = userId;
    recoveryCache.timestamp = now;

    return result;
  } catch (error) {
    console.error('🩺 getRecoveryHistory: Error loading from Dexie:', error);
    return [];
  }
}

// DEPRECATED: Old localStorage version - keeping for reference
function getRecoveryHistoryFromLocalStorage(): RecoveryMetrics[] {
  const stored = storage.get('recoveryHistory');
  if (!stored) {
    return [];
  }
  
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      console.error('🩺 getRecoveryHistory: Data is not an array, clearing corrupted data');
      storage.remove('recoveryHistory');
      return [];
    }

    // Convert date strings back to Date objects
    const migrated = parsed.map((m: any) => ({
      ...m,
      date: new Date(m.date)
    }));

    console.log(`🩺 getRecoveryHistory: Loaded ${migrated.length} recovery entries`);
    return migrated;
  } catch (error) {
    console.error('🩺 getRecoveryHistory: Parse error', error);
    console.log('� Clearing corrupted recovery data');
    storage.remove('recoveryHistory');
    return [];
  }
}

/**
 * Get today's recovery metrics (if logged)
 */
export async function getTodayRecoveryMetrics(): Promise<RecoveryMetrics | null> {
  const history = await getRecoveryHistory();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return history.find(m => {
    const metricDate = new Date(m.date);
    metricDate.setHours(0, 0, 0, 0);
    return metricDate.getTime() === today.getTime();
  }) || null;
}

/**
 * Single source-of-truth readiness lookup — consolidates the two
 * readiness models (Audit #8). Pre-2026-06-07 the workout generator
 * read `latestRecovery.readinessScore` directly, which is the value
 * blended-and-stored at SAVE time. If fresh wearable data has landed
 * overnight (Garmin sync, HealthKit refresh, new HRV reading), the
 * stored score is stale by the morning.
 *
 * getCurrentReadiness():
 *   1. Loads today's RecoveryMetrics (subjective survey)
 *   2. Loads the user's CURRENT PersonalState (fresh wearable signals)
 *   3. Re-blends via calculateReadinessScore so the result reflects
 *      both subjective and the latest objective data
 *   4. Returns the blended score plus the source RecoveryMetrics
 *      (callers that need raw subjective inputs still have them)
 *
 * Callers should prefer this over reading `latestRecovery.readinessScore`
 * directly. The stored field stays accurate for trend/history reads;
 * for "what's the user's readiness RIGHT NOW," use this function.
 */
export async function getCurrentReadiness(): Promise<{
  score: number;
  metrics: RecoveryMetrics | null;
  source: 'fresh-blend' | 'stored' | 'default';
} | null> {
  const metrics = await getTodayRecoveryMetrics();
  if (!metrics) {
    return { score: 70, metrics: null, source: 'default' };
  }

  // Try to load fresh PersonalState and re-blend. Falls back to the
  // stored score if PersonalState isn't available (anonymous user, no
  // wearable data, network failure).
  try {
    const { loadPersonalState } = await import('./personalState');
    const userId = getCurrentUserId();
    if (userId) {
      const personalState = await loadPersonalState(userId, null);
      if (personalState) {
        const fresh = await calculateReadinessScore(metrics, personalState);
        return { score: fresh.score, metrics, source: 'fresh-blend' };
      }
    }
  } catch {
    // Fall through to stored.
  }

  return { score: metrics.readinessScore ?? 70, metrics, source: 'stored' };
}


/**
 * Retroactive blend backfill — recompute readinessScore on existing
 * recovery entries using the v1.9.14+ blended formula. Entries logged
 * before that release have a subjective-only score; this brings them
 * forward so trend lines and history reads are consistent.
 *
 * Idempotent — recomputes from the same inputs every time. Safe to
 * call multiple times. Returns the number of entries updated.
 *
 * Strategy:
 *   1. Pull all recovery entries
 *   2. For each, re-run calculateReadinessScore (now auto-blends)
 *   3. If the score changed AND the entry has no blend metadata stamped,
 *      persist the new score
 *
 * We only touch entries lacking the `blend` field — once blended, we
 * don't recompute (avoid drift if PersonalState shifts further later).
 */
export async function backfillBlendedReadiness(): Promise<{ scanned: number; updated: number; errors: number }> {
  if (typeof window === 'undefined') return { scanned: 0, updated: 0, errors: 0 };
  const history = await getRecoveryHistory();
  let scanned = 0;
  let updated = 0;
  let errors = 0;

  for (const m of history) {
    scanned++;
    // Skip already-blended entries (presence of any non-zero score
    // computed AFTER v1.9.14 wouldn't be detectable without a marker
    // — we use a Dexie field `blendApplied` to gate idempotency).
    if ((m as any).blendApplied) continue;
    try {
      const fresh = await calculateReadinessScore(m);
      // Only persist when the score actually changed — saves a write
      // round-trip on entries where blend didn't apply (e.g. confidence
      // too low at the time).
      if (Math.abs((m.readinessScore ?? 0) - fresh.score) >= 1) {
        const patched: RecoveryMetrics = {
          ...m,
          readinessScore: fresh.score,
        };
        // Stamp the blendApplied marker so this entry isn't rewritten
        // again next backfill run.
        (patched as any).blendApplied = true;
        await saveRecoveryMetrics(patched);
        updated++;
      } else {
        // Stamp marker anyway — confirms we've scanned this entry
        const noOp: RecoveryMetrics = { ...m };
        (noOp as any).blendApplied = true;
        await saveRecoveryMetrics(noOp);
      }
    } catch (e) {
      console.warn(`[backfill] failed for ${m.id}:`, e);
      errors++;
    }
  }

  return { scanned, updated, errors };
}
